import { useRef, useState, useEffect, useCallback } from 'react';
import { isAxiosError } from 'axios';
import { useParams, Navigate } from 'react-router-dom';
import { Typography, CircularProgress, Alert, Snackbar, Box } from '@mui/material';
import {
  AuditHistorySection,
  DelegationApprovalBanner,
  JobEndFooter,
  JobEndPdfContent,
  ProjectDetailsSection,
  NatureOfRoleSection,
  SkillRatingsSection,
  StrengthsDevelopmentSection,
  SeniorTeamFeedbackSection,
  ValidationSummaryBanner,
} from '@/components/job-ends';
import { useJobEndDetails, useEligibleDelegates, useJobEndAuditHistory } from '@/queries/useJobEnds';
import { useSeniorFeedbacks } from '@/queries/useSeniorFeedback';
import { useJobEndAccess } from '@/queries/usePermissions';
import { generateJobEndPdf } from '@/utils/generateJobEndPdf';
import { formatDate } from '@/utils/dateFormatter';
import { useAuthStore } from '@/stores/useAuthStore';
import type { EligibleDelegate, SkillDisplayMode } from '@/api/types/jobEnd.types';
import { validateJobEnd, NO_ERRORS } from '@/components/job-ends/jobEndValidation';
import type { ValidationErrors } from '@/components/job-ends/jobEndValidation';

export function JobEndPage() {
  const { id } = useParams<{ id: string }>();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(NO_ERRORS);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const natureOfRoleRef = useRef<HTMLDivElement>(null);
  const skillRatingsRef = useRef<HTMLDivElement>(null);
  const strengthsDevRef = useRef<HTMLDivElement>(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);

  const { data: jobEnd, isLoading, error } = useJobEndDetails(id || '');
  const { data: permissions, isError: isPermissionsError, error: permissionsError } = useJobEndAccess(id || '');
  const currentStaffId = useAuthStore((state) => state.permissions?.staffId);

  const { data: delegates } = useEligibleDelegates(
    id || '',
    permissions?.actions.canDelegate || false
  );

  const canDownload = permissions?.actions.canDownload || false;
  const showSeniorFeedbackForPdf = permissions?.sections.seniorFeedback?.access !== 'none' && permissions?.sections.seniorFeedback?.access !== undefined;
  const showAuditHistoryForPdf = permissions?.sections.auditHistory?.access === 'read';
  const { data: pdfSeniorFeedbacks = [] } = useSeniorFeedbacks(Number(id), canDownload && showSeniorFeedbackForPdf);
  const { data: pdfAuditRecords = [] } = useJobEndAuditHistory(id || '', canDownload && showAuditHistoryForPdf);

  const validationErrorsRef = useRef(validationErrors);
  validationErrorsRef.current = validationErrors;
  const permissionsRef = useRef(permissions);
  permissionsRef.current = permissions;

  const handleValidationErrors = useCallback((errors: ValidationErrors) => {
    setValidationErrors(errors);
    const hasErrors = errors.natureOfRole || errors.skillRatings || errors.strengthsDev;
    setShowValidationBanner(hasErrors);
    if (hasErrors) {
      setScrollTrigger(prev => prev + 1);
    }
  }, []);

  const handleDownloadPdf = useCallback(() => {
    if (!jobEnd) return;
    setIsGeneratingPdf(true);
  }, [jobEnd]);

  useEffect(() => {
    if (!isGeneratingPdf || !pdfContentRef.current || !jobEnd) return;
    const timer = setTimeout(async () => {
      try {
        const filename = `Job_End_${jobEnd.appraiseeFirstName}_${jobEnd.appraiseeLastName}_${jobEnd.projectCode}.pdf`;
        await generateJobEndPdf(pdfContentRef.current!, filename);
      } catch {
        setSnackbar({ open: true, message: 'Failed to generate PDF', severity: 'error' });
      } finally {
        setIsGeneratingPdf(false);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isGeneratingPdf, jobEnd]);

  useEffect(() => {
    if (!showValidationBanner || !jobEnd) return;
    const natureOfRoleAccess = permissions?.sections.natureOfRole?.access;
    const isNatureOfRoleVisible = natureOfRoleAccess !== 'none' && natureOfRoleAccess !== undefined;
    const skillRatingsAccess = permissions?.sections.ratingsBySkill;
    const isSkillRatingsVisible = skillRatingsAccess?.access !== 'none' && skillRatingsAccess?.access !== undefined;
    const errors = validateJobEnd(jobEnd, isNatureOfRoleVisible, isSkillRatingsVisible && !!jobEnd.skillRatings);
    setValidationErrors(errors);
    const hasErrors = errors.natureOfRole || errors.skillRatings || errors.strengthsDev;
    if (!hasErrors) {
      setShowValidationBanner(false);
    }
  }, [jobEnd, showValidationBanner, permissions]);

  useEffect(() => {
    if (scrollTrigger === 0) return;
    const p = permissionsRef.current;
    const errors = validationErrorsRef.current;
    const natureOfRoleAccess = p?.sections.natureOfRole?.access;
    const isNatureOfRoleVisible = natureOfRoleAccess !== 'none' && natureOfRoleAccess !== undefined;
    const skillRatingsAccess = p?.sections.ratingsBySkill;
    const isSkillRatingsVisible = skillRatingsAccess?.access !== 'none' && skillRatingsAccess?.access !== undefined;
    const sectionRefs = [
      { error: errors.natureOfRole && isNatureOfRoleVisible, ref: natureOfRoleRef },
      { error: errors.skillRatings && isSkillRatingsVisible, ref: skillRatingsRef },
      { error: errors.strengthsDev, ref: strengthsDevRef },
    ];
    const firstError = sectionRefs.find((s) => s.error);
    if (firstError?.ref.current) {
      firstError.ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [scrollTrigger]);

  const snackbarComponent = (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
    </Snackbar>
  );

  const isAccessRevokedError = (err: unknown) =>
    isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 403);

  if (isLoading) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
        {snackbarComponent}
      </>
    );
  }

  if (isAccessRevokedError(error) || isAccessRevokedError(permissionsError)) {
    return <Navigate to="/" replace />;
  }

  if (error || !jobEnd) {
    return <Navigate to="/error" replace />;
  }

  const assignmentDate = jobEnd.assignmentEndDate
    ? `${formatDate(jobEnd.assignmentStartDate)} — ${formatDate(jobEnd.assignmentEndDate)}`
    : formatDate(jobEnd.assignmentStartDate);

  const projectDetails = {
    appraiseeName: `${jobEnd.appraiseeFirstName} ${jobEnd.appraiseeLastName}`,
    jobEndAuthor: `${jobEnd.collatorFirstName} ${jobEnd.collatorLastName}`,
    assignmentDate,
    tenureInGrade: jobEnd.tenureInGrade,
    clientName: jobEnd.clientName,
    projectName: jobEnd.projectName,
    projectCode: jobEnd.projectCode,
    delegationStatus: jobEnd.delegationStatus,
    delegateName:
      jobEnd.delegateFirstName && jobEnd.delegateLastName
        ? `${jobEnd.delegateFirstName} ${jobEnd.delegateLastName}`
        : undefined,
    delegateExpiry: jobEnd.delegateExpiry,
  };

  const formattedDelegates: { id: string; name: string; staffId: number }[] =
    delegates?.map((delegate: EligibleDelegate) => ({
      id: String(delegate.staffId),
      name: `${delegate.firstName} ${delegate.lastName}`,
      staffId: delegate.staffId,
    })) || [];

  const natureOfRoleAccess = permissions?.sections.natureOfRole?.access;
  const canEditNatureOfRole = natureOfRoleAccess === 'edit';
  const showNatureOfRole = natureOfRoleAccess !== 'none' && natureOfRoleAccess !== undefined;

  const skillRatingsAccess = permissions?.sections.ratingsBySkill;
  const canEditSkillRatings = skillRatingsAccess?.access === 'edit';
  const showSkillRatings = skillRatingsAccess?.access !== 'none' && skillRatingsAccess?.access !== undefined;
  const skillRatingsDisplayMode = (skillRatingsAccess?.displayMode || 'full') as SkillDisplayMode;

  const strengthsDevAccess = permissions?.sections.strengthsDevelopmentFeedback?.access;
  const canEditStrengthsDev = strengthsDevAccess === 'edit';
  const showStrengthsDev = strengthsDevAccess !== 'none' && strengthsDevAccess !== undefined;

  const seniorFeedbackAccess = permissions?.sections.seniorFeedback?.access;
  const showSeniorFeedback = seniorFeedbackAccess !== 'none' && seniorFeedbackAccess !== undefined;

  const auditHistoryAccess = permissions?.sections.auditHistory?.access;
  const showAuditHistory = auditHistoryAccess === 'read';

  const showDelegationBanner =
    jobEnd.delegationStatus === 'Pending' && jobEnd.delegateStaffId === currentStaffId;
  const collatorName = `${jobEnd.collatorFirstName} ${jobEnd.collatorLastName}`;

  return (
    <>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Job End
      </Typography>
      {isPermissionsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load permissions: {permissionsError?.message || 'Unknown error'}
        </Alert>
      )}
      {showDelegationBanner && (
        <DelegationApprovalBanner
          jobEndId={Number(id)}
          collatorName={collatorName}
          setSnackbar={setSnackbar}
          rowVersion={jobEnd.rowVersion}
        />
      )}
      {showValidationBanner && (validationErrors.natureOfRole || validationErrors.skillRatings || validationErrors.strengthsDev) && (
        <ValidationSummaryBanner
          validationErrors={validationErrors}
          showNatureOfRole={showNatureOfRole}
          showSkillRatings={showSkillRatings && !!jobEnd.skillRatings}
        />
      )}
      <ProjectDetailsSection
        projectDetails={projectDetails}
        delegates={formattedDelegates}
        canDelegate={permissions?.actions.canDelegate || false}
        showDelegationStatus={permissions?.role === 'Collator'}
        jobEndId={Number(id)}
        setSnackbar={setSnackbar}
        rowVersion={jobEnd.rowVersion}
      />
      {showNatureOfRole && (
        <div ref={natureOfRoleRef}>
          <NatureOfRoleSection
            activities={jobEnd.natureOfRoleActivities || []}
            activityRatings={jobEnd.activityRatings || []}
            canEdit={canEditNatureOfRole}
            jobEndId={Number(id)}
            error={validationErrors.natureOfRole}
            rowVersion={jobEnd.rowVersion}
          />
        </div>
      )}
      {showSkillRatings && jobEnd.skillRatings && (
        <div ref={skillRatingsRef}>
          <SkillRatingsSection
            skillRatings={jobEnd.skillRatings}
            canEdit={canEditSkillRatings}
            displayMode={skillRatingsDisplayMode}
            jobEndId={Number(id)}
            error={validationErrors.skillRatings}
            rowVersion={jobEnd.rowVersion}
          />
        </div>
      )}
      {showStrengthsDev && (
        <div ref={strengthsDevRef}>
          <StrengthsDevelopmentSection
            strengths={jobEnd.strengthFeedbacks || []}
            developmentAreas={jobEnd.developmentAreaFeedbacks || []}
            canEdit={canEditStrengthsDev}
            jobEndId={Number(id)}
            error={validationErrors.strengthsDev}
            rowVersion={jobEnd.rowVersion}
          />
        </div>
      )}
      {showSeniorFeedback && (
        <SeniorTeamFeedbackSection
          jobEndId={Number(id)}
          earliestDeadline={jobEnd.seniorFeedbackEarliestDeadline}
          access={seniorFeedbackAccess as 'edit' | 'read'}
          role={permissions?.role || ''}
          rowVersion={jobEnd.rowVersion}
        />
      )}
      {showAuditHistory && <AuditHistorySection jobEndId={Number(id)} />}
      <JobEndFooter
        jobEndId={Number(id)}
        permissions={permissions}
        jobEnd={jobEnd}
        showNatureOfRole={showNatureOfRole}
        showSkillRatings={showSkillRatings && !!jobEnd.skillRatings}
        setSnackbar={setSnackbar}
        setValidationErrors={handleValidationErrors}
        onDownloadPdf={handleDownloadPdf}
        isGeneratingPdf={isGeneratingPdf}
      />
      {isGeneratingPdf && (
        <div
          ref={pdfContentRef}
          style={{ position: 'fixed', left: '-9999px', top: 0, width: '794px' }}
        >
          <JobEndPdfContent
            jobEnd={jobEnd}
            showNatureOfRole={showNatureOfRole}
            showSkillRatings={showSkillRatings && !!jobEnd.skillRatings}
            skillRatingsDisplayMode={skillRatingsDisplayMode}
            seniorFeedbacks={pdfSeniorFeedbacks}
            showSeniorFeedback={showSeniorFeedback}
            showStrengthsDev={showStrengthsDev}
            auditRecords={pdfAuditRecords}
            showAuditHistory={showAuditHistory}
          />
        </div>
      )}
      {snackbarComponent}
    </>
  );
}

export default JobEndPage;
